import type { Dispatch, ReactNode, SetStateAction } from 'react';
import type {
  AdminAccess,
  AiAccess,
  Contact,
  CreateContactInput,
  SupportTicketAccess,
} from '@catalyst-psa/shared';
import {
  ADMIN_ACCESS_LABELS,
  AI_ACCESS_LABELS,
  SUPPORT_TICKET_ACCESS_LABELS,
  portalRoleFlagLabel,
} from '@catalyst-psa/shared';

import { Badge } from '../../components/ui/Badge.js';
import { CheckboxField, TextField } from '../../components/ui/Field.js';
import { useServiceCategoryOptions } from '../service-categories/api.js';
import { MailerliteStatusBadge } from './MailerliteStatusBadge.js';

/** Editable contact fields shared by the customer Contacts tab and the Contacts page. */
export interface ContactFormState {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  title: string;
  sendToMailerlite: boolean;
  billingContact: boolean;
  projectContact: boolean;
  supportContact: boolean;
  ticketManager: boolean;
  supportTicketAccess: SupportTicketAccess;
  aiAccess: AiAccess;
  adminAccess: AdminAccess;
  catPlusNotifications: boolean;
  referenceable: boolean;
  lastReferenceRequest: string;
  notificationServiceCategoryIds: string[];
  netsuiteEmployeeId: string;
  isPrimary: boolean;
  isActive: boolean;
}

export const EMPTY_CONTACT: ContactFormState = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  title: '',
  sendToMailerlite: false,
  billingContact: false,
  projectContact: false,
  supportContact: false,
  ticketManager: false,
  supportTicketAccess: 'no_access',
  aiAccess: 'no_access',
  adminAccess: 'no_access',
  catPlusNotifications: false,
  referenceable: false,
  lastReferenceRequest: '',
  notificationServiceCategoryIds: [],
  netsuiteEmployeeId: '',
  isPrimary: false,
  isActive: true,
};

export function toContactFormState(contact: Contact): ContactFormState {
  return {
    firstName: contact.firstName,
    lastName: contact.lastName ?? '',
    email: contact.email ?? '',
    phone: contact.phone ?? '',
    title: contact.title ?? '',
    sendToMailerlite: contact.sendToMailerlite,
    billingContact: contact.billingContact,
    projectContact: contact.projectContact,
    supportContact: contact.supportContact,
    ticketManager: contact.ticketManager,
    supportTicketAccess: contact.supportTicketAccess,
    aiAccess: contact.aiAccess,
    adminAccess: contact.adminAccess,
    catPlusNotifications: contact.catPlusNotifications,
    referenceable: contact.referenceable,
    lastReferenceRequest: contact.lastReferenceRequest ?? '',
    notificationServiceCategoryIds: contact.notificationServiceCategoryIds,
    netsuiteEmployeeId: contact.netsuiteEmployeeId ?? '',
    isPrimary: contact.isPrimary,
    isActive: contact.isActive,
  };
}

/** The request body shared by create and update (create adds `customerId`). */
export function contactFormPayload(form: ContactFormState): Omit<CreateContactInput, 'customerId'> {
  return {
    firstName: form.firstName.trim(),
    lastName: form.lastName.trim(),
    email: form.email.trim(),
    phone: form.phone.trim() ? form.phone.trim() : null,
    title: form.title.trim() ? form.title.trim() : null,
    sendToMailerlite: form.sendToMailerlite,
    billingContact: form.billingContact,
    projectContact: form.projectContact,
    supportContact: form.supportContact,
    ticketManager: form.ticketManager,
    supportTicketAccess: form.supportTicketAccess,
    aiAccess: form.aiAccess,
    adminAccess: form.adminAccess,
    catPlusNotifications: form.catPlusNotifications,
    referenceable: form.referenceable,
    lastReferenceRequest: form.lastReferenceRequest ? form.lastReferenceRequest : null,
    notificationServiceCategoryIds: form.notificationServiceCategoryIds,
    netsuiteEmployeeId: form.netsuiteEmployeeId.trim() ? form.netsuiteEmployeeId.trim() : null,
    isPrimary: form.isPrimary,
    isActive: form.isActive,
  };
}

interface ContactFieldsProps {
  form: ContactFormState;
  setForm: Dispatch<SetStateAction<ContactFormState>>;
  fieldErrors: Record<string, string>;
  /** The saved contact, when editing — used for the read-only Catalyst Portal mirror. */
  contact?: Contact | null;
}

function Section({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: ReactNode;
}): JSX.Element {
  return (
    <section className="space-y-3 rounded-lg border border-slate-200 p-3">
      <div>
        <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
        {description ? <p className="text-xs text-slate-500">{description}</p> : null}
      </div>
      {children}
    </section>
  );
}

function ReadOnlyValue({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-slate-400">{label}</div>
      <div className="text-sm text-slate-700">{value}</div>
    </div>
  );
}

function ReadOnlyFlag({ label, value }: { label: string; value: boolean }): JSX.Element {
  return (
    <div className="flex items-center justify-between gap-2">
      <span className="text-sm text-slate-600">{label}</span>
      <Badge tone={value ? 'green' : 'gray'}>{value ? 'Yes' : 'No'}</Badge>
    </div>
  );
}

/** The contact field body (no header/footer) so every contact editor stays in sync. */
export function ContactFields({
  form,
  setForm,
  fieldErrors,
  contact,
}: ContactFieldsProps): JSX.Element {
  const serviceCategoriesQuery = useServiceCategoryOptions();
  const serviceCategoryOptions = serviceCategoriesQuery.data ?? [];

  return (
    <div className="space-y-4">
      <Section title="Overview">
        <div className="grid grid-cols-2 gap-3">
          <TextField
            label="First name"
            required
            value={form.firstName}
            maxLength={100}
            onChange={(e) => setForm((f) => ({ ...f, firstName: e.target.value }))}
            error={fieldErrors.firstName}
          />
          <TextField
            label="Last name"
            required
            value={form.lastName}
            maxLength={100}
            onChange={(e) => setForm((f) => ({ ...f, lastName: e.target.value }))}
            error={fieldErrors.lastName}
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <TextField
            label="Email"
            type="email"
            required
            value={form.email}
            onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
            error={fieldErrors.email}
          />
          <TextField
            label="Phone"
            value={form.phone}
            maxLength={64}
            onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
            error={fieldErrors.phone}
          />
        </div>
        <TextField
          label="Title"
          value={form.title}
          maxLength={150}
          onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
          error={fieldErrors.title}
        />
        <div className="grid grid-cols-2 gap-2">
          <CheckboxField
            label="Primary contact"
            checked={form.isPrimary}
            onChange={(e) => setForm((f) => ({ ...f, isPrimary: e.target.checked }))}
          />
          <CheckboxField
            label="Billing contact"
            checked={form.billingContact}
            onChange={(e) => setForm((f) => ({ ...f, billingContact: e.target.checked }))}
          />
          <div className="flex items-center gap-2">
            <CheckboxField
              label="Send to MailerLite"
              checked={form.sendToMailerlite}
              onChange={(e) => setForm((f) => ({ ...f, sendToMailerlite: e.target.checked }))}
            />
            {contact ? <MailerliteStatusBadge contact={contact} placeholder="Not sent" /> : null}
          </div>
        </div>
      </Section>

      {contact ? (
        <Section
          title="Catalyst Portal (read-only)"
          description="This is only a view of the portal config. Changes are made by customer self administration or in the portal directly."
        >
          <PortalSnapshot contact={contact} />
        </Section>
      ) : null}

      <Section title="Notifications">
        <CheckboxField
          label="Cat+ Overage Notification"
          checked={form.catPlusNotifications}
          onChange={(e) => setForm((f) => ({ ...f, catPlusNotifications: e.target.checked }))}
        />
        <div>
          <span className="text-sm font-medium text-slate-700">
            This contact will receive notifications about the following (coming soon)
          </span>
          <div className="mt-1 grid grid-cols-2 gap-x-4 gap-y-1 rounded-lg border border-slate-200 bg-white p-3">
            {serviceCategoryOptions.length === 0 ? (
              <p className="col-span-2 text-xs text-slate-400">No service categories configured.</p>
            ) : (
              serviceCategoryOptions.map((category) => (
                <CheckboxField
                  key={category.id}
                  label={category.name}
                  checked={form.notificationServiceCategoryIds.includes(category.id)}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      notificationServiceCategoryIds: e.target.checked
                        ? [...f.notificationServiceCategoryIds, category.id]
                        : f.notificationServiceCategoryIds.filter((id) => id !== category.id),
                    }))
                  }
                />
              ))
            )}
          </div>
        </div>
      </Section>

      <Section title="Other">
        <div className="grid grid-cols-2 gap-3">
          <TextField
            label="Last reference request"
            type="date"
            value={form.lastReferenceRequest}
            onChange={(e) => setForm((f) => ({ ...f, lastReferenceRequest: e.target.value }))}
            error={fieldErrors.lastReferenceRequest}
          />
          <TextField
            label="NetSuite Employee ID"
            value={form.netsuiteEmployeeId}
            maxLength={64}
            onChange={(e) => setForm((f) => ({ ...f, netsuiteEmployeeId: e.target.value }))}
            error={fieldErrors.netsuiteEmployeeId}
          />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <CheckboxField
            label="Referenceable"
            checked={form.referenceable}
            onChange={(e) => setForm((f) => ({ ...f, referenceable: e.target.checked }))}
          />
          <CheckboxField

            label="Active"
            checked={form.isActive}
            onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))}
          />
        </div>
      </Section>

      <Section
        title="Onboarding (read-only)"
        description="What this contact asked for on the welcome form. Reference only — it grants nothing."
      >
        <div className="grid gap-3 sm:grid-cols-3">
          <ReadOnlyValue
            label="Support Ticket Access"
            value={SUPPORT_TICKET_ACCESS_LABELS[form.supportTicketAccess]}
          />
          <ReadOnlyValue label="AI Access" value={AI_ACCESS_LABELS[form.aiAccess]} />
          <ReadOnlyValue label="Admin Access" value={ADMIN_ACCESS_LABELS[form.adminAccess]} />
        </div>
        <div className="grid gap-x-6 gap-y-1 sm:grid-cols-3">
          <ReadOnlyFlag label="Ticket manager" value={form.ticketManager} />
          <ReadOnlyFlag label="Project contact" value={form.projectContact} />
          <ReadOnlyFlag label="Support contact" value={form.supportContact} />
        </div>
      </Section>
    </div>
  );
}

/** Read-only mirror of the Catalyst Portal user this contact resolves to. */
function PortalSnapshot({ contact }: { contact: Contact }): JSX.Element {
  const flagKeys = Object.keys(contact.portalRoleFlags).sort();
  const matched = contact.portalUserId !== null || flagKeys.length > 0;

  if (!matched) {
    return <p className="text-sm text-slate-500">This contact was not found in the portal.</p>;
  }

  return (
    <>
      <div className="flex flex-wrap items-center gap-2">
        <Badge tone={contact.portalStatus === 'active' ? 'green' : 'gray'}>
          {contact.portalStatus ?? 'unknown status'}
        </Badge>
        <span className="text-xs text-slate-400">
          Portal user {contact.portalUserId ?? '—'} · Tenant {contact.portalTenant ?? '—'} · Last
          synced {contact.portalSyncedAt ? new Date(contact.portalSyncedAt).toLocaleString() : 'never'}
        </span>
      </div>
      <dl className="grid gap-x-6 gap-y-1 sm:grid-cols-2">
        {flagKeys.map((key) => (
          <div key={key} className="flex items-center justify-between gap-2 text-sm">
            <dt className="text-slate-600">{portalRoleFlagLabel(key)}</dt>
            <dd>
              <Badge tone={contact.portalRoleFlags[key] ? 'green' : 'gray'}>
                {contact.portalRoleFlags[key] ? 'On' : 'Off'}
              </Badge>
            </dd>
          </div>
        ))}
      </dl>
    </>
  );
}
